import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { MessageSquare, UserX } from "lucide-react";
import { cn } from "@/lib/utils";

export type TrainerInfo = {
  name: string;
  avatarUrl: string | null;
  initials: string;
};

type TrainerChatSidebarProps = {
  trainer: TrainerInfo | null;
  trainerResolved: boolean;
  trainerId: string | null;
  className?: string;
};

export function TrainerChatSidebar({
  trainer,
  trainerResolved,
  trainerId,
  className,
}: TrainerChatSidebarProps) {
  return (
    <aside
      className={cn(
        "hidden xl:flex flex-col rounded-2xl border border-primary/20 bg-dark-card shadow-card overflow-hidden flex-shrink-0 w-[320px]",
        className
      )}
    >
      <div className="p-6 border-b border-primary/15">
        <h2 className="text-sm font-semibold text-gray-light uppercase tracking-wide mb-4">
          Your trainer
        </h2>

        {!trainerResolved ? (
          <p className="text-sm text-gray-light">Loading…</p>
        ) : !trainerId ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 rounded-xl bg-dark-slate border border-primary/10 flex items-center justify-center mx-auto mb-3">
              <UserX className="w-6 h-6 text-gray-light" />
            </div>
            <p className="text-white font-medium text-sm">No trainer assigned</p>
            <p className="text-xs text-gray-light mt-1">Contact support for help.</p>
          </div>
        ) : (
          <div className="flex flex-col items-center text-center">
            <Avatar className="w-20 h-20 ring-2 ring-primary/30 mb-3">
              {trainer?.avatarUrl ? (
                <AvatarImage src={trainer.avatarUrl} alt={trainer.name} />
              ) : (
                <AvatarFallback className="bg-dark-slate text-white text-xl font-semibold">
                  {trainer?.initials || "TR"}
                </AvatarFallback>
              )}
            </Avatar>
            <p className="text-lg font-semibold text-white">{trainer?.name || "Your Trainer"}</p>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="mt-4 border-primary/30 text-primary hover:bg-primary hover:text-black"
            >
              <Link to="/client-profile#trainer">View trainer profile</Link>
            </Button>
          </div>
        )}
      </div>

      <div className="p-6 flex-1">
        <div className="flex items-start gap-3 rounded-xl bg-dark-slate/50 border border-primary/10 p-4">
          <MessageSquare className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-white mb-1">About messaging</p>
            <p className="text-xs text-gray-light leading-relaxed">
              Message your trainer anytime with questions about workouts, form, or your program.
              They typically respond within one business day.
            </p>
          </div>
        </div>
      </div>
    </aside>
  );
}
